/* @flow */

import React from 'react';
import {connect} from 'react-redux';
import moment from 'moment';

import LoadingComponent from 'components/loading';
import SellInvoiceBatchTableViewComponent from './tableView';
import SellInvoiceBatchFormViewComponent from './formView';

import DebtorCompany from 'models/companies/debtorCompany';

import InsertInvoiceRequest from 'models/requests/sellers/insertInvoiceRequest';
import InsertBatchInvoiceRequest, {BatchInvoiceEntryRequest} from 'models/requests/sellers/insertBatchInvoiceRequest';
import ValidateReferencesRequest from 'models/requests/sellers/validateReferencesRequest';

import InvoiceSellerService from 'services/InvoiceSellerService';
import {handleApiFormResponse} from 'lib/forms';
import {validateForm} from 'lib/validation/validator';
import loadConstraints from 'lib/validation/loadConstraints';

const VIEW_TABLE = 'table';
const VIEW_FORM = 'form';

type SellInvoiceBatchComponentProps = {
  activeCompanyId: number;
  debtorCompany: DebtorCompany;
  successHandler: (debtorCompany: DebtorCompany, invoiceId: number) => void;
  cancelInvoice: () => void;
}

class SellInvoiceBatchComponent extends React.Component<SellInvoiceBatchComponentProps> {

  constructor(props) {
    super(props);

    this.state = {
      isLoading: true,
      isProcessing: false,
      invoiceInfo: null,
      constraints: null,
      entries: [],
      view: VIEW_TABLE,
      editingIndex: null,
      form: this.emptyForm(),
      errors: new Map(),
      formErrors: []
    };
  }

  emptyForm = () => ({reference: '', debtorReference: '', debtAmount: '', issueDate: null, dueDate: null});

  componentDidMount = async () => {
    const invoiceInfo = await InvoiceSellerService.getInvoiceInfo(this.props.activeCompanyId, this.props.debtorCompany.id);

    const constraints = await loadConstraints('Invoice', ['debtAmount', 'reference', 'dueDate', 'issueDate']);
    constraints.dueDate.dateInMoreThanXDays.days = invoiceInfo.maxDaysToSell;
    constraints.dueDate.dateInLessThanXDays.days = invoiceInfo.minDaysToSell;
    // No minimum-amount validation on a single batch entry (ticket) - only require a positive amount
    constraints.debtAmount = {
      presence: {allowEmpty: false, message: 'form.mandatoryField'},
      numericality: {greaterThan: 0, notGreaterThan: 'form.mandatoryField'}
    };
    // Client reference is required on the batch entry form
    constraints.debtorReference = {
      presence: {allowEmpty: false, message: 'form.mandatoryField'}
    };

    this.setState({invoiceInfo, constraints, isLoading: false});
  }

  sum = () => this.state.entries.reduce((acc, e) => acc + Number(e.debtAmount || 0), 0);

  // ----- table view actions -----
  goToAddForm = () => this.setState({view: VIEW_FORM, editingIndex: null, form: this.emptyForm(), errors: new Map(), formErrors: []});
  editEntry = (index: number) => this.setState({view: VIEW_FORM, editingIndex: index, form: {...this.state.entries[index]}, errors: new Map(), formErrors: []});
  removeEntry = (index: number) => {
    const entries = this.state.entries.slice();
    entries.splice(index, 1);
    this.setState({entries});
  }
  cancelBatch = () => {
    this.setState({isProcessing: true});
    this.props.cancelInvoice();
  }

  // ----- entry form actions -----
  handleChange = (fieldName: string) => (event) => this.setFormField(fieldName, event.target.value);
  handleDateChange = (fieldName: string) => (date) => this.setFormField(fieldName, date);
  setFormField = (fieldName: string, value: any) => {
    const form = {...this.state.form, [fieldName]: value};
    const errors = new Map(this.state.errors);
    errors.set(fieldName, undefined);
    this.setState({form, errors});
  }

  cancelEntryForm = () => this.setState({view: VIEW_TABLE, editingIndex: null, errors: new Map(), formErrors: []});

  validateEntry = async () => {
    const errors = new Map();
    let valid = true;

    // field-level validation
    const results = validateForm(this.state.form, this.state.constraints);
    if (results && Object.keys(results).length) {
      valid = false;
      Object.keys(results).forEach(k => errors.set(k, results[k][0].split(' ').pop()));
    }

    // uniqueness against the other entries already in the table
    const ref = (this.state.form.reference || '').trim().toUpperCase();
    const cref = (this.state.form.debtorReference || '').trim().toUpperCase();
    this.state.entries.forEach((e, i) => {
      if (i === this.state.editingIndex) return;
      if (ref && (e.reference || '').trim().toUpperCase() === ref) {
        errors.set('reference', 'page.sellInvoice.tab2.batch.validation.referenceNotUnique');
        valid = false;
      }
      if (cref && (e.debtorReference || '').trim().toUpperCase() === cref) {
        errors.set('debtorReference', 'page.sellInvoice.tab2.batch.validation.clientReferenceNotUnique');
        valid = false;
      }
    });

    if (!valid) {
      this.setState({errors});
      return false;
    }

    // uniqueness against existing invoices in Pam X
    const result = await InvoiceSellerService.validateReferences(
      new ValidateReferencesRequest(this.props.activeCompanyId, this.state.form.reference, this.state.form.debtorReference));
    if (!result.referenceUnique) {
      errors.set('reference', 'page.sellInvoice.tab2.batch.validation.referenceNotUnique');
      valid = false;
    }
    if (!result.debtorReferenceUnique) {
      errors.set('debtorReference', 'page.sellInvoice.tab2.batch.validation.clientReferenceNotUnique');
      valid = false;
    }

    if (!valid) this.setState({errors});
    return valid;
  }

  formToEntry = () => ({
    reference: this.state.form.reference,
    debtorReference: this.state.form.debtorReference,
    debtAmount: Number(this.state.form.debtAmount),
    issueDate: this.state.form.issueDate,
    dueDate: this.state.form.dueDate
  });

  addEntrySameDebtor = async () => {
    this.setState({isProcessing: true});
    try {
      if (!(await this.validateEntry())) {
        this.setState({isProcessing: false});
        return;
      }

      const entries = this.state.entries.slice();
      if (this.state.editingIndex !== null) {
        entries[this.state.editingIndex] = this.formToEntry();
        this.setState({entries, view: VIEW_TABLE, editingIndex: null, isProcessing: false});
      } else {
        entries.push(this.formToEntry());
        this.setState({entries, form: this.emptyForm(), errors: new Map(), isProcessing: false});
      }
    } catch (e) {
      handleApiFormResponse.call(this, e);
    }
  }

  submitSingle = async () => {
    this.setState({isProcessing: true});
    try {
      if (!(await this.validateEntry())) {
        this.setState({isProcessing: false});
        return;
      }

      const request = new InsertInvoiceRequest();
      request.sellerCompanyId = this.props.activeCompanyId;
      request.debtorCompanyId = this.props.debtorCompany.id;
      request.reference = this.state.form.reference;
      request.debtorReference = this.state.form.debtorReference;
      request.debtAmount = Number(this.state.form.debtAmount);
      request.issueDate = moment(this.state.form.issueDate).format('YYYY/MM/DD');
      request.dueDate = moment(this.state.form.dueDate).format('YYYY/MM/DD');

      const invoiceId = await InvoiceSellerService.insertInvoice(request);
      this.props.successHandler(this.props.debtorCompany, invoiceId);
    } catch (e) {
      handleApiFormResponse.call(this, e);
    }
  }

  submitBatch = async () => {
    this.setState({isProcessing: true});
    try {
      const request = new InsertBatchInvoiceRequest();
      request.sellerCompanyId = this.props.activeCompanyId;
      request.debtorCompanyId = this.props.debtorCompany.id;
      request.entries = this.state.entries.map(e => {
        const entry = new BatchInvoiceEntryRequest();
        entry.reference = e.reference;
        entry.debtorReference = e.debtorReference;
        entry.debtAmount = e.debtAmount;
        entry.issueDate = moment(e.issueDate).format('YYYY/MM/DD');
        entry.dueDate = moment(e.dueDate).format('YYYY/MM/DD');
        return entry;
      });

      const invoiceId = await InvoiceSellerService.insertBatchInvoice(request);
      this.props.successHandler(this.props.debtorCompany, invoiceId);
    } catch (e) {
      handleApiFormResponse.call(this, e);
    }
  }

  render() {
    if (this.state.isLoading) {
      return (<LoadingComponent />);
    }

    if (this.state.view === VIEW_FORM) {
      return (
        <SellInvoiceBatchFormViewComponent
          form={this.state.form}
          errors={this.state.errors}
          formErrors={this.state.formErrors}
          invoiceInfo={this.state.invoiceInfo}
          minDebtAmount={this.state.invoiceInfo.minDebtAmount}
          isProcessing={this.state.isProcessing}
          isEditing={this.state.editingIndex !== null}
          handleChange={this.handleChange}
          handleDateChange={this.handleDateChange}
          cancelInvoice={this.cancelEntryForm}
          addEntrySameDebtor={this.addEntrySameDebtor}
          submitSingle={this.submitSingle}
        />
      );
    }

    return (
      <SellInvoiceBatchTableViewComponent
        entries={this.state.entries}
        sum={this.sum()}
        minDebtAmount={this.state.invoiceInfo.minDebtAmount}
        isProcessing={this.state.isProcessing}
        formErrors={this.state.formErrors}
        addInvoice={this.goToAddForm}
        editEntry={this.editEntry}
        removeEntry={this.removeEntry}
        cancelInvoice={this.cancelBatch}
        submitBatch={this.submitBatch}
      />
    );
  }
}

const mapStateToProps = state => ({
  activeCompanyId: state.auth.user.activeCompany.id
});

export default connect(mapStateToProps)(SellInvoiceBatchComponent);
